// src/components/reports/ReportCard.jsx

import React, { useState, useRef, useEffect } from 'react';
import { Flag, Ban, MoreVertical, Forward, History, BookmarkPlus, AlertTriangle, Save, User } from 'lucide-react';
import StatusBadge from './StatusBadge';

const ReportCard = ({ report, onReview, onRemove, onAction = () => {} }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    console.log('[ReportCard] Dropdown state changed:', {
      isOpen: isDropdownOpen,
      reportId: report._id,
      contentType: report.contentType,
      status: report.status
    });
  }, [isDropdownOpen, report.contentType, report._id, report.status]);  

  const handleAction = (action) => {
    console.log('[ReportCard] Action triggered:', action, 'for report:', report._id);
    setIsDropdownOpen(false);
    onAction(report._id, action);
  };

  const handleMoreClick = () => {
    console.log('[ReportCard] More button clicked', {
      reportId: report._id,
      contentType: report.contentType,
      status: report.status,
      isCurrentlyOpen: isDropdownOpen
    });
    setIsDropdownOpen(!isDropdownOpen);
  };

    // Determine which actions are available based on report status
    const getAvailableActions = () => {
      const baseActions = [
        {
          id: 'view_history',
          label: 'View History',
          icon: History,
          available: true
        },
        // Add this new action for resolved reports
        ...(report.status === 'resolved' ? [{
          id: 'unresolve',
          label: 'Unresolve Report',
          icon: AlertTriangle,
          available: true
        }] : []),
        // Only show these for non-resolved reports
        ...(report.status !== 'resolved' ? [
          {
            id: 'save_for_later',
            label: 'Save for Later',
            icon: Save,
            available: report.status === 'pending' || report.status === 'in_review'
          },
          {
            id: 'escalate',
            label: 'Escalate Priority',
            icon: AlertTriangle,
            available: report.status === 'pending' || report.status === 'in_review'
          },
          {
            id: 'forward',
            label: 'Forward to Admin',
            icon: Forward,
            available: report.status === 'pending' || report.status === 'in_review'
          },
          {
            id: 'add_to_batch',
            label: 'Add to Batch',
            icon: BookmarkPlus,
            available: report.status === 'pending' || report.status === 'in_review'
          }
        ] : [])
      ];
    
      return baseActions;
    };    
  
    const availableActions = getAvailableActions();

    const renderPreviewImage = () => {
      console.log('[ReportCard - Rendering preview image]', {
        contentType: report.contentType,
        hasItemImage: report.contentType === 'GoGet' ? !!report.contentId?.images?.item?.url : undefined,
        hasProfileImage: report.contentType === 'User' ? !!report.contentId?.profilePhotoUrl : undefined,
        contentId: report.contentId
      });
  
      if (report.contentType === 'GoGet' && report.contentId?.images?.item?.url) {
        return (
          <div className="relative">
            <img 
              src={report.contentId.images.item.url} 
              alt="Content preview" 
              className="w-full h-48 object-cover"
            />
            <div className="absolute top-2 right-2">
              <StatusBadge status={report.status} />
            </div>
          </div>
        );
      } else if (report.contentType === 'User' && report.contentId?.profilePhotoUrl) {
        return (
          <div className="relative">
            <img 
              src={report.contentId.profilePhotoUrl} 
              alt={`${report.contentId.username}'s profile`} 
              className="w-full h-48 object-cover"
            />
            <div className="absolute top-2 right-2">
              <StatusBadge status={report.status} />
            </div>
          </div>
        );
      } else if (report.contentType === 'User') {
        // Placeholder for users without profile photos
        return (
          <div className="relative w-full h-48 bg-gray-100 flex items-center justify-center">
            <User className="h-20 w-20 text-gray-400" />
            <div className="absolute top-2 right-2">
              <StatusBadge status={report.status} />
            </div>
          </div>
        );
      }
      
      return null;
    };
  
    const getContentTitle = () => {
      if (report.contentType === 'GoGet') {
        return report.contentId?.itemName;
      } else if (report.contentType === 'User') {
        return `@${report.contentId?.username || 'Unknown User'}`;
      }
      return 'User Report';
    };
  
  

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Preview Image Section */}
      {renderPreviewImage()}

      {/* Content Info */}
      <div className="p-4">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {getContentTitle()}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>Reported by @{report.reportedBy?.username}</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p className="font-medium">Reason: {report.reason.replace('_', ' ')}</p>
            {report.details && <p className="mt-1">{report.details}</p>}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 text-red-600">
                <Flag className="h-4 w-4" />
                <span className="text-sm">{report.relatedReports?.length || 0} related reports</span>
              </div>
              <span className="text-sm text-gray-500">
                {new Date(report.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
        <div className="flex space-x-2">
          {report.status === 'resolved' ? (
            // Show Unresolve button for resolved reports
            <button 
              onClick={() => onAction(report._id, 'unresolve')}
              className="px-3 py-1 text-sm text-yellow-600 hover:bg-yellow-50 rounded flex items-center space-x-1"
            >
              <AlertTriangle className="h-4 w-4" />
              <span>Unresolve</span>
            </button>
          ) : (
            // Show normal action buttons for unresolved reports
            <>
              <button 
                onClick={() => onReview(report)}
                className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                Review
              </button>
              <button 
                onClick={() => onRemove(report._id)}
                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded flex items-center space-x-1"
              >
                <Ban className="h-4 w-4" />
                <span>Remove</span>
              </button>
            </>
          )}
        </div>
        
        {/* Custom Dropdown */}
        <div className="relative inline-block text-left" ref={dropdownRef}> 
          <button 
            onClick={handleMoreClick}
            className="p-1 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-200"
              style={{ filter: 'drop-shadow(0 20px 13px rgb(0 0 0 / 0.03)) drop-shadow(0 8px 5px rgb(0 0 0 / 0.08))' }}
            >
              <div className="py-1 bg-white rounded-md">
                <div className="px-3 py-2 text-sm font-medium text-gray-900 flex items-center justify-between">
                  <span>Report Actions</span>
                  {report.status === 'resolved' && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      Resolved
                    </span>
                  )}
                </div>
                <div className="h-px bg-gray-200 my-1"></div>
                
                {availableActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleAction(action.id)}
                    className={`flex w-full items-center px-3 py-2 text-sm 
                      ${action.available 
                        ? 'text-gray-700 hover:bg-gray-100' 
                        : 'text-gray-400 cursor-not-allowed'}`}
                    disabled={!action.available}
                  >
                    <action.icon className="mr-2 h-4 w-4" />
                    {action.label}
                    {!action.available && report.status === 'resolved' && (
                      <span className="ml-auto text-xs text-gray-400">
                        Report resolved
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportCard;